import type { CycleEvidenceResponse, GridLevelResponse } from "@/shared/api";

/** Legacy aggregate retained only for API responses with no evidence envelope. */
const DEPTH_BANDS = 4;

const LEVEL_TONE: Record<GridLevelResponse["state"], string> = {
  FILLED: "border-status-positive bg-status-positive",
  PARTIALLY_FILLED: "border-status-warning bg-status-warning",
  UNFILLED: "border-border-strong bg-surface-default",
  CANCELLED: "border-text-muted bg-surface-hover",
};

function markerPosition(evidence: CycleEvidenceResponse): number | null {
  const placement = evidence.placement;
  if (placement === null || placement.levels.length === 0) return null;
  const position = placement.current_price_position;
  const indexOf = (levelIndex: number | null): number | null => {
    if (levelIndex === null) return null;
    const found = placement.levels.findIndex((level) => level.level_index === levelIndex);
    return found < 0 ? null : found;
  };
  const denominator = Math.max(1, placement.levels.length - 1);
  const percent = (index: number): number => (index / denominator) * 100;
  const at = indexOf(position.at_level_index);
  if (at !== null) return percent(at);
  const lower = indexOf(position.lower_level_index);
  const upper = indexOf(position.upper_level_index);
  if (lower !== null && upper !== null) return (percent(lower) + percent(upper)) / 2;
  if (position.relation === "ABOVE_GRID") return 0;
  if (position.relation === "BELOW_GRID") return 100;
  return null;
}

function ExactGrid({ evidence, slotId }: { readonly evidence: CycleEvidenceResponse; readonly slotId: string }): React.JSX.Element {
  const placement = evidence.placement;
  if (evidence.coverage.grid === "unavailable" || placement === null) {
    return (
      <section data-testid={`grid-progress-${slotId}`} className="min-w-0">
        <span className="block text-label-caps tracking-[0.08em] text-text-muted">
          Сетка · выставленные ордера
        </span>
        <span className="mt-2 block text-helper-sm text-text-muted">Покрытие данных недоступно</span>
      </section>
    );
  }
  const complete = evidence.coverage.grid === "complete" && evidence.coverage.fills === "complete";
  const marker = markerPosition(evidence);
  const position = placement.current_price_position;
  return (
    <section data-testid={`grid-progress-${slotId}`} className="min-w-0">
      <div className="mb-2 flex items-baseline justify-between gap-3">
        {/* Named for what it actually contains. The recovery zones are not in
            it and are drawn separately, because a zone is a price being watched
            and an order is capital already committed. */}
        <span className="text-label-caps tracking-[0.08em] text-text-muted">
          Сетка · выставленные ордера
        </span>
        <span className={`font-mono text-helper-sm tabular-nums ${complete ? "text-text-secondary" : "text-status-warning"}`}>
          {complete ? `${String(placement.filled_level_count)} / ${String(placement.planned_level_count)} исполнено` : "Данные неполные"}
        </span>
      </div>
      <div className="relative pt-3">
        <span aria-hidden="true" className="absolute inset-x-1 top-[1.125rem] h-px bg-border-strong" />
        {marker !== null && (
          <span
            data-testid={`grid-current-price-${slotId}`}
            data-relation={position.relation}
            title={position.current_price === null ? "Текущая цена недоступна" : `Текущая цена ${position.current_price}`}
            style={{ left: `${String(marker)}%` }}
            className="absolute top-0 z-[2] -translate-x-1/2 text-[9px] text-action-primary"
          >▼</span>
        )}
        <div className="relative flex items-center justify-between">
          {placement.levels.map((level) => {
            const price = level.planned_order_price ?? level.reference_price;
            return (
              <span key={level.level_index} className="relative z-[1] flex min-w-0 flex-col items-center">
                <span
                  data-testid={`grid-level-${slotId}-${String(level.level_index)}`}
                  data-state={level.state}
                  title={`Level ${String(level.level_index)} · ${level.state} · planned ${price ?? "—"} · filled ${level.executed_quantity}${level.weighted_fill_price === null ? "" : ` @ ${level.weighted_fill_price}`}`}
                  className={`size-3 rounded-full border-2 transition-colors ${LEVEL_TONE[level.state]}`}
                />
                <span className="sr-only">Level {String(level.level_index)} {level.state}</span>
              </span>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function LegacyDepth({ depth, slotId }: { readonly depth: number | undefined; readonly slotId: string }): React.JSX.Element {
  const recorded = depth ?? 0;
  const bounded = Math.min(DEPTH_BANDS, Math.max(0, Math.trunc(recorded)));
  return (
    <section data-testid={`grid-progress-${slotId}`} title={`Записанная глубина сетки: ${String(recorded)}; визуальная шкала ограничена ${String(DEPTH_BANDS)} диапазонами просадки`} className="min-w-0">
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <span className="text-label-caps tracking-[0.08em] text-text-muted">GRID DEPTH</span>
        <span className="font-mono text-helper-sm tabular-nums text-text-secondary">{String(bounded)} / {String(DEPTH_BANDS)}</span>
      </div>
      <div role="progressbar" aria-label="Зафиксированная глубина сетки" aria-valuemin={0} aria-valuemax={DEPTH_BANDS} aria-valuenow={bounded} className="relative flex items-center justify-between">
        <span aria-hidden="true" className="absolute inset-x-1 top-1/2 h-px -translate-y-1/2 bg-border-strong" />
        {Array.from({ length: DEPTH_BANDS }, (_, index) => <span key={index} data-testid={`grid-level-${slotId}-${String(index + 1)}`} aria-hidden="true" className={`relative z-[1] size-2.5 rounded-full border ${index < bounded ? "border-action-primary bg-action-primary" : "border-border-strong bg-surface-default"}`} />)}
      </div>
    </section>
  );
}

export function GridProgress({ depth, evidence, slotId }: { readonly depth: number | undefined; readonly evidence: CycleEvidenceResponse | null | undefined; readonly slotId: string }): React.JSX.Element {
  return evidence == null ? <LegacyDepth depth={depth} slotId={slotId} /> : <ExactGrid evidence={evidence} slotId={slotId} />;
}
